import { useRef, useCallback, useEffect } from 'react';

interface UseInfiniteScrollProps {
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
  root?: Element | null;
}

/**
 * A custom hook that implements infinite scrolling functionality.
 * 
 * @param options - Configuration options for the infinite scroll behavior
 * @param options.loading - Flag indicating if initial data is loading
 * @param options.loadingMore - Flag indicating if more data is being loaded
 * @param options.hasMore - Flag indicating if there is more data to load
 * @param options.onLoadMore - Callback function to trigger when more data should be loaded
 * @param options.rootMargin - Margin around the root element (default: '200px')
 * @param options.threshold - Visibility threshold to trigger loading (default: 0.1)
 * @param options.enabled - Flag to enable/disable the infinite scroll (default: true)
 * @returns Object containing refs and utility methods for infinite scrolling
 */
const useInfiniteScroll = ({
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  rootMargin = '200px', 
  threshold = 0.1,
  enabled = true,
  root = null
}: UseInfiniteScrollProps) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const loadingTriggerRef = useRef<HTMLDivElement | null>(null);

  // Setup the intersection observer
  const setupIntersectionObserver = useCallback(() => {
    // Don't set up if loading, already loading more, or there's no more data
    if (loading || loadingMore || !hasMore || !enabled) return;
    
    // Disconnect previous observer if it exists
    if (observer.current) {
      observer.current.disconnect();
    }
    
    // Create a new observer
    observer.current = new IntersectionObserver(entries => {
      // If the loading trigger element is visible and we have more data to load
      if (entries[0]?.isIntersecting && hasMore && !loadingMore && enabled) {
        onLoadMore();
      }
    }, {
      root,
      rootMargin,
      threshold
    });
    
    // Observe the loading trigger element
    if (loadingTriggerRef.current) {
      observer.current.observe(loadingTriggerRef.current);
    }
  }, [loading, loadingMore, hasMore, onLoadMore, rootMargin, threshold, enabled, root]);
  
  // Set up and clean up the observer
  useEffect(() => {
    setupIntersectionObserver();
    
    // Clean up observer on unmount
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [setupIntersectionObserver]);

  return {
    loadingTriggerRef,
    shouldDisplayLoadingTrigger: hasMore && !loading && enabled,
    shouldDisplayLoadingMore: loadingMore,
    shouldDisplayEndMessage: !hasMore && !loading && !loadingMore,
    shouldDisplayEmptyMessage: !loading && !loadingMore
  };
};

export default useInfiniteScroll; 